/**
 * useCheckupReuse (BL-214)
 *
 * Shared across the three CheckUp widgets (pitch / language / motivational).
 * Inside a CheckUp assessment, if the talent already has a REUSABLE banked
 * result for this test unit (source `testit` OR a prior CheckUp — the store
 * holds the latest either way), the widget offers to reuse it instead of forcing
 * a fresh capture. The banked data already lives where MESH reads it (the pitch
 * vault file, the analyzed language files, the hydrated motivational answers),
 * so "reuse" is simply completing the step — no re-upload.
 *
 * `isReusable`, not `isValid`. That premise — "the banked data already lives
 * where MESH reads it" — is FALSE for a self-declared level, which has no
 * analyzed artifact behind it at all. `isValid` only answers "is it inside its
 * validity window", so filtering on it offered a citizen who typed
 * "Português — native" at onboarding the chance to reuse a Portuguese test they
 * had never taken, in the middle of the assessment that was asking for it. The
 * backend now draws that distinction (`isResultReusable` in @tonnie/heroes);
 * this hook must not re-derive it — badges and thresholds come from the
 * backend.
 *
 * Only active in CheckUp mode; a standalone TestIt run passes `enabled: false`
 * (there the catalog modal already owns the reuse/history UX).
 */

import { useMemo } from 'react';
import { useTalentTestResults, type TalentTestResultView } from '@tonnie/sdk';

export function useCheckupReuse(
  testId: string | null,
  talentId: string,
  options: { enabled: boolean }
): { reusable: TalentTestResultView | null; isLoading: boolean } {
  const enabled = options.enabled && !!testId && !!talentId;

  // Resilient by design: a failed/empty read just means "no reuse offered" and
  // the widget falls back to its normal capture flow — never blocks the step.
  const { data, isLoading } = useTalentTestResults(talentId, { enabled });

  const reusable = useMemo(() => {
    if (!enabled || !testId || !data) return null;
    return data.find((r) => r.testId === testId && r.isReusable) ?? null;
  }, [enabled, testId, data]);

  return { reusable, isLoading: enabled ? isLoading : false };
}
